<template>
    <div class="notify" v-bind="$attrs">
        <TransitionGroup :name="animation" appear>
            <Notice v-for="node in noticeNodes" :key="node.key" :node="node" />
        </TransitionGroup>
    </div>
</template>
<script>
import { createApp, h } from 'vue';
import { CONFIG_PROPS, ICON_TYPES } from './utils';
import Notice from './Notice';

let seed = 0;
const now = Date.now();
const config = CONFIG_PROPS();

function getUuid() {
    const id = seed;
    seed += 1;
    return `notify_${now}_${id}`;
}

const Notify = {
    name: 'Notify',
    inheritAttrs: false,
    props: {
        prefixCls: {
            type: String,
            default: config.prefixCls,
        },
        maxCount: {
            type: Number,
            default: config.maxCount,
        },
        closeIcon: {
            type: Object,
            default: () => config.closeIcon,
        },
        animation: {
            type: String,
            default: 'fade',
        },
    },
    components: {
        Notice,
    },
    data() {
        return {
            hookRefs: new Map(),
            notices: [],
        };
    },
    computed: {
        noticeNodes() {
            const { prefixCls, closeIcon } = this;
            const result = this.notices.map(({ notice, holderCb }, idx) => {
                const updateMark = idx === this.notices.length - 1 ? notice.updateMark : undefined;
                const { key, userPassKey, message, description, type } = notice;
                const classes = [prefixCls, notice.class];

                if (ICON_TYPES.includes(type)) classes.push(`is-${type}`);

                return {
                    ...notice.props,
                    prefixCls,
                    classes,
                    closeIcon,
                    key,
                    message,
                    description,
                    noticeKey: userPassKey || key,
                    updateMark,
                    holderCb,
                    onClick: notice.onClick,
                    onClose: (noticeKey) => {
                        this.remove(noticeKey);
                        notice.onClose();
                    },
                };
            });
            return result;
        },
    },
    methods: {
        add(origin, holderCb) {
            const key = origin.key || getUuid();
            const notice = { ...origin, key };
            const noticeIndex = this.notices.map((v) => v.notice.key).indexOf(key);
            const updatedNotices = this.notices.slice();
            if (noticeIndex !== -1) {
                this.updatedNotices.splice(noticeIndex, 1, { notice, holderCb });
            } else {
                if (this.maxCount && this.notices.length >= this.maxCount) {
                    notice.key = updatedNotices[0].notice.key;
                    notice.updateMark = getUuid();
                    notice.userPassKey = key;
                    updatedNotices.shift();
                }
                updatedNotices.push({ notice, holderCb });
            }
            this.notices = updatedNotices;

            setTimeout(() => {
                this.notices = this.notices.filter((n) => n.notice.key !== key);
            }, config.duration);
        },
        remove(removeKey) {
            this.notices = this.notices.filter(({ notice: { key, userPassKey } }) => {
                const mergedKey = userPassKey || key;
                return mergedKey !== removeKey;
            });
        },
    },
};

Notify.newInstance = function createNotifyInstance(properties = {}, cb = () => {}) {
    const {
        name = 'notify',
        getContainer,
        context,
        appContext,
        prefixCls: customizePrefixCls,
        ...props
    } = properties;

    const div = document.createElement('div');
    const prefixCls = customizePrefixCls;
    const root = getContainer();

    let notifyRef = null;

    const app = createApp({
        mounted() {
            notifyRef = this.$refs.notifyRef;
            cb({
                notice(noticeProps) {
                    notifyRef.add(noticeProps);
                },
                removeNotice(key) {
                    notifyRef.remove(key);
                },
                destroy() {
                    app.unmount();
                    if (div.parentNode) {
                        div.parentNode.removeChild(div);
                    }
                },
                component: notifyRef,
            });
        },
        render() {
            return h(Notify, {
                ref: 'notifyRef',
                prefixCls,
                ...props,
            });
        },
    });

    root.appendChild(div);
    app.mount(div);
};

export default Notify;
</script>

<style lang="scss" src="./Notify.scss"></style>
